import { useIntl } from 'react-intl';

import { CreditSummary } from '~/features/billing/CreditBalance/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';

export const useGetModelsSummary = () => {
  const { data: summary, isLoading } =
    api.endpoints.getModelSummary.useQuery(undefined);

  return {
    isLoading,
    summary: {
      total: summary?.total ?? 0,
      published: summary?.published ?? 0,
      suspended: summary?.suspended ?? 0,
      deleted: summary?.deleted ?? 0,
      draft: summary?.draft ?? 0,
      consumedQuota: summary?.consumed_quota ?? 0,
      totalQuota: summary?.total_quota ?? 0,
      deletedPublishedModels: summary?.deleted_after_published ?? 0,
    },
  };
};

export const useGetCreditsTooltip = ({
  credits,
  recurringCredits,
  balances,
}: CreditSummary): string => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const secondaryinfo =
    credits <= 0
      ? intl.formatMessage({
          id: 'billingUtils.noCreditsAvailable',
          defaultMessage:
            'There are no Credits Available. Contact Evisort to purchase additional credits. You can save models as drafts, but not publish any models if you have a negative credit balance. Credits can be used to create Custom AI models. One credit equals one model applied to one contract.',
          description: 'Message when no credits are available',
        })
      : null;

  const recurringBalance = balances?.find((balance) => balance.renewalDate);
  const formattedRecurringCredits = recurringCredits
    ? formatNumber(recurringBalance?.credits ?? 0)
    : null;
  const formattedRemainingCredits =
    recurringBalance && recurringBalance.remainingCredits > 0
      ? formatNumber(recurringBalance.remainingCredits)
      : 0;

  const formattedDate = recurringCredits
    ? formatDate(
        recurringCredits.renewalDate,
        hasLocalization ? 'short' : 'iso',
      )
    : null;

  const renewalInfo = formattedRecurringCredits
    ? intl.formatMessage(
        {
          id: 'billingUtils.recurringCreditsRenewal',
          defaultMessage:
            '{remainingCredits} Recurring Credits will reset to {recurringCredits} on {renewalDate}.',
          description:
            'Information about recurring credits renewal. Example: remainingCredits = "50", recurringCredits = "100", renewalDate = "January 10, 2025"',
        },
        {
          remainingCredits: formattedRemainingCredits,
          recurringCredits: formattedRecurringCredits,
          renewalDate: formattedDate,
        },
      )
    : '';

  const balanceCreditsMap = balances?.reduce<Map<string, number>>(
    (acc, curr) => {
      if (curr.renewalDate || !curr.expirationDate) {
        return acc;
      }
      const formattedExpirationDate = formatDate(
        curr.expirationDate,
        hasLocalization ? 'short' : 'iso',
      );
      const currentCredits = acc.get(formattedExpirationDate);
      if (currentCredits) {
        acc.set(
          formattedExpirationDate,
          currentCredits + curr.remainingCredits,
        );
      } else {
        acc.set(formattedExpirationDate, curr.remainingCredits);
      }
      return acc;
    },
    new Map(),
  );

  let creditExpirationInfo = '';

  if (balanceCreditsMap) {
    balanceCreditsMap.forEach((credits, expirationDate) => {
      if (credits > 0) {
        const creditExpirationMessage = intl.formatMessage(
          {
            id: 'billingUtils.creditsExpiration',
            defaultMessage: '{credits} Credits will expire on {expirationDate}',
            description:
              'Message about credits expiration date. Example: credits = "5,000", expirationDate = "December 31, 2024"',
          },
          {
            credits: formatNumber(credits),
            expirationDate,
          },
        );
        creditExpirationInfo = `${creditExpirationInfo}${creditExpirationMessage}`;
      }
    });
  }

  return `${renewalInfo}${
    creditExpirationInfo
      ? `${renewalInfo ? '\n' : ''}${creditExpirationInfo}`
      : ''
  }${secondaryinfo ? `${renewalInfo ? '\n\n' : ''}${secondaryinfo}` : ''}`;
};

export const useGetEstimatedCreditsTooltip = (
  credits: number,
  estimatedCredits: number,
): string => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();

  const exceedCreditsTooltip = intl.formatMessage({
    id: 'billingUtils.insufficientCredits',
    defaultMessage:
      'You do not have sufficient credits to publish this model. When the estimated credits exceed credits available, you can save models as drafts, but not publish any models. Adjust the scope or contact Evisort to purchase additional credits.',
    description: 'Message when user has insufficient credits to publish model',
  });

  const estimatedCreditsTooltip = intl.formatMessage(
    {
      id: 'billingUtils.estimatedCreditsUsage',
      defaultMessage:
        'This model will utilize approximately {estimatedCredits} credits on publishing. Additional credits may be used as new documents are uploaded that fit within the scope selected.',
      description:
        'Message about estimated credits usage for model. Example: estimatedCredits = "1,000"',
    },
    {
      estimatedCredits: formatNumber(estimatedCredits),
    },
  );

  const zeroCreditsTooltip = intl.formatMessage({
    id: 'billingUtils.zeroEstimatedCredits',
    defaultMessage:
      'Estimated Credits are 0 because there are no documents in scope. Adjust the number of documents in scope by clicking on the filters below scope.',
    description:
      'Message when estimated credits is zero due to no documents in scope',
  });

  return estimatedCredits > credits
    ? exceedCreditsTooltip
    : estimatedCredits === 0
      ? zeroCreditsTooltip
      : estimatedCreditsTooltip;
};
